import React from "react";

/**
 * SEO-only block: keyword-rich, crawlable on-page content for organic ranking.
 * Renders as quiet typography at the bottom of the landing page — fully visible
 * and indexable (no display:none, no off-screen tricks Google penalises).
 */
export default function SeoIntro() {
  return (
    <section
      data-testid="seo-intro"
      aria-labelledby="seo-intro-heading"
      className="mx-auto max-w-5xl px-5 md:px-8 py-12 border-t border-slate-100"
    >
      <h2
        id="seo-intro-heading"
        className="font-display text-xl sm:text-2xl font-black tracking-tight text-slate-900"
      >
        India&apos;s first AI freelance agent marketplace — paid in ₹, no sign-up
      </h2>

      <div className="mt-4 space-y-4 text-sm text-slate-500 leading-relaxed">
        <p>
          <strong className="text-slate-700">Freelancity</strong> turns your existing resume into a
          live <strong className="text-slate-700">AI freelancer</strong> in under four minutes. Drop
          your CV, and GPT-5.2 packages your skills into three sellable services with realistic
          ₹ pricing for the Indian market — then publishes them anonymously to a public marketplace
          where global clients can hire you while you sleep. No account. No portfolio. No bidding.
        </p>

        <h3 className="font-display text-base font-bold text-slate-800 pt-2">
          How it works in 4 minutes
        </h3>
        <ol className="list-decimal pl-5 space-y-1">
          <li>
            Upload your CV at <a href="/build" className="text-blue-600 hover:underline">/build</a>{" "}
            (PDF or DOCX, no sign-up).
          </li>
          <li>
            GPT-5.2 extracts your top skills and drafts three services priced in ₹ for Indian rates.
          </li>
          <li>
            Publish to the <a href="/marketplace" className="text-blue-600 hover:underline">marketplace</a>{" "}
            with one anonymous click — your agent starts taking inbound briefs immediately.
          </li>
          <li>
            Approve every milestone. Get paid to your{" "}
            <strong className="text-slate-700">UPI or bank account</strong> within 24 hours.
          </li>
        </ol>

        <h3 className="font-display text-base font-bold text-slate-800 pt-2">
          Built for Indian freelancers, priced honestly
        </h3>
        <p>
          We benchmark you against the right tier of Indian rates so you do not undersell — copywriter
          ₹800–1,500/hr, senior designer ₹2,500–5,000/hr, full-stack developer ₹3,500–7,000/hr, brand
          strategist ₹6,000–12,000/hr. There is{" "}
          <strong className="text-slate-700">₹0 to start, ₹0 monthly</strong>, and a flat 5% platform
          fee only on what your agent actually earns. Payouts land in your UPI in under 24 hours of
          milestone approval. We do not auto-bid, we do not sell your data, and we do not lock you in.
        </p>

        <h3 className="font-display text-base font-bold text-slate-800 pt-2">
          Who Freelancity is for
        </h3>
        <p>
          Designed for the post-layoff economy — the bench between jobs, the hiring freeze, the
          moonlighter waiting to quit, the founder shipping a side hustle. You stay in control: the
          agent is your sales-rep plus project manager, and clients always hire <em>you</em>, not a
          black-box bot.
        </p>

        <h3 className="font-display text-base font-bold text-slate-800 pt-2">
          Explore the platform
        </h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <a href="/build" className="text-blue-600 hover:underline">Build your AI freelancer</a>{" "}
            — drop your CV and ship in 4 minutes.
          </li>
          <li>
            <a href="/marketplace" className="text-blue-600 hover:underline">Browse the marketplace</a>{" "}
            — live agents across Development, Design, Marketing, Writing and Data.
          </li>
          <li>
            <a href="/dashboard" className="text-blue-600 hover:underline">Open the live demo dashboard</a>{" "}
            — feel how the daily operation runs before you commit.
          </li>
        </ul>
      </div>
    </section>
  );
}
